import React from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Brain, FileText, Camera, CheckSquare,
  MessageSquare, Mail, FileSignature, Download,
} from "lucide-react";

/* Quick Actions row — Property Workspace toolbar.
   Every button now performs a real action instead of a toast placeholder.
   Most jump to the relevant tab; Export PDF hits the report endpoint. */
const ACTIONS = [
  { id: "analyze",           label: "Analyze Deal",      icon: Brain,          tab: "deal_score" },
  { id: "create_offer",      label: "Create Offer",      icon: FileText,       tab: "offers" },
  { id: "upload_photos",     label: "Upload Photos",     icon: Camera,         tab: "photos" },
  { id: "add_task",          label: "Add Task",          icon: CheckSquare,    tab: "tasks" },
  { id: "send_sms",          label: "Send SMS",          icon: MessageSquare,  tab: "communications" },
  { id: "send_email",        label: "Send Email",        icon: Mail,           tab: "communications" },
  { id: "generate_contract", label: "Generate Contract", icon: FileSignature,  tab: "documents" },
  { id: "export_pdf",        label: "Export PDF",        icon: Download,       tab: null },  // handled inline
];

export default function QuickActions({ property, tabs, onSelectTab }) {
  const enabledIds = new Set((tabs || []).filter((t) => t.enabled).map((t) => t.id));

  const handleExportPdf = async () => {
    // Prefer a legacy `linked_lead_id` if present (older properties came from
    // a lead conversion), but fall back to the v3 property id — the backend
    // now resolves either against `leads` OR `properties` and synthesizes
    // a lead-shaped payload from a property record when needed.
    const reportId = property?.linked_lead_id || property?.id;
    if (!reportId) {
      toast.info("Save the property first — then the Analysis Report becomes available.");
      return;
    }
    toast.loading("Generating your Property Analysis Report…", { id: "pdf-gen" });
    try {
      await api.post(`/leads/${reportId}/report/generate`);
      const res = await api.get(`/leads/${reportId}/report.pdf`, {
        responseType: "blob", timeout: 60000,
      });
      // Guard: some upstream gateways return HTML error pages with 200 status.
      // If we didn't actually get a PDF, treat it as a failure.
      const ct = res.headers?.["content-type"] || res.data?.type || "";
      if (!ct.includes("pdf")) {
        const asText = typeof res.data.text === "function" ? await res.data.text() : "";
        throw new Error(asText?.slice(0, 240) || `Unexpected response type: ${ct}`);
      }
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeAddr = (property?.address?.street || "property")
        .replace(/[^\w-]+/g, "-").slice(0, 60);
      a.download = `iSpeedToClose-${safeAddr}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF ready — check your downloads", { id: "pdf-gen" });
    } catch (err) {
      // With `responseType: "blob"`, axios returns error bodies AS BLOBS —
      // so `err.response.data.detail` is undefined. Read the blob back as
      // JSON to surface the real backend error message to Tracy.
      let detail = err?.message;
      const data = err?.response?.data;
      if (data && typeof data.text === "function") {
        try {
          const text = await data.text();
          try {
            const parsed = JSON.parse(text);
            detail = parsed?.detail || parsed?.message || text.slice(0, 240);
          } catch {
            detail = text.slice(0, 240);
          }
        } catch { /* ignore blob-read failure */ }
      } else if (typeof data === "object" && data?.detail) {
        detail = data.detail;
      }
      const status = err?.response?.status;
      const msg = detail
        ? (status ? `PDF error ${status}: ${detail}` : `PDF error: ${detail}`)
        : "Could not export PDF — check console for details.";
      toast.error(msg, { id: "pdf-gen", duration: 8000 });
      console.error("[export-pdf] failed", { reportId, status, detail, err });
    }
  };

  const handleClick = (a) => {
    if (a.id === "export_pdf") return handleExportPdf();
    if (!a.tab) return;
    if (!enabledIds.has(a.tab)) {
      toast.info(`${a.label} — this tab isn't available for this property yet.`);
      return;
    }
    onSelectTab?.(a.tab);
    // Scroll to the tab bar so the user sees the switch immediately.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="workspace-tab-bar"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  return (
    <div
      data-testid="workspace-quick-actions"
      className="flex flex-wrap gap-2"
      role="toolbar"
      aria-label="Quick actions"
    >
      {ACTIONS.map((a) => (
        <button
          key={a.id}
          type="button"
          data-testid={`quick-action-${a.id}`}
          onClick={() => handleClick(a)}
          className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1.5
                     text-[12px] text-slate-900 hover:text-slate-900 hover:border-amber-300 hover:bg-amber-50 transition-colors"
        >
          <a.icon size={13} />
          <span>{a.label}</span>
        </button>
      ))}
    </div>
  );
}
